// Per-frame opaque-pixel bounding box.
// trimRect is in absolute sheet coordinates; localOffset is relative to the cell.

import type { Rect } from '~/types/pipeline';

export type TrimResult = {
  trimRect: Rect;
  localOffset: { x: number; y: number };
  sourceSize: { w: number; h: number };
  empty: boolean;
};

/**
 * Compute the tight bounding box of opaque pixels (alpha >= alphaThreshold)
 * within the given cell rectangle of an ImageData.
 *
 * trimRect is in absolute sheet coordinates.
 * localOffset is relative to the cell's top-left corner.
 */
export function trimFrame(imageData: ImageData, cell: Rect, alphaThreshold = 1): TrimResult {
  const { data, width } = imageData;
  const { x: sx, y: sy, w: sw, h: sh } = cell;

  let minX = sw;
  let minY = sh;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < sw; x++) {
      const a = data[((sy + y) * width + (sx + x)) * 4 + 3] ?? 0;
      if (a >= alphaThreshold) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < 0) {
    return {
      trimRect: { x: sx, y: sy, w: 0, h: 0 },
      localOffset: { x: 0, y: 0 },
      sourceSize: { w: sw, h: sh },
      empty: true,
    };
  }

  return {
    trimRect: { x: sx + minX, y: sy + minY, w: maxX - minX + 1, h: maxY - minY + 1 },
    localOffset: { x: minX, y: minY },
    sourceSize: { w: sw, h: sh },
    empty: false,
  };
}
